import React from 'react'
import PropTypes from 'prop-types'
import { Container, Card, Heading } from '@scentregroup/ui'
import classNames from 'classnames'
import PotentialLink from '../auto-potential-link'
import Article from '../article'
import containerStyle from '../container.scss'
import RetailerUtilityBox from '../../components/retailer-utility-box'
import Markdown from '../markdown'
import CentreUtilityBox from '../centre-utility-box'
import PatternSVG from '../../assets/images/pattern.inline.svg'
import { CustomThemeContext } from '../../context'
import ResponsiveAsset from '../responsive-asset'
import matchMedia from '../../helpers/match-media'
import Trackable from '../trackable'
import { trackingActions, trackingTypes } from '../../constants'
import { occurrencesType, callToActionType } from './../../types'
import style from './index.scss'
import wMaskStyle from './w-mask.scss'
import WMask from './w-mask.inline.svg'

const MAXIMUM_MASK_LOAD_TIME = 3500

class Feature extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      showMask: props.withMask,
      animateMask: false,
    }
    this.maskLoadTimeout = null
    this.showMaskQuery = props.withMask
      ? matchMedia('(orientation: portrait)')
      : null
  }

  componentDidMount() {
    // Make sure the mask goes away if the feature image takes too long to load
    if (this.props.withMask) {
      this.maskLoadTimeout = setTimeout(this.hideMask, MAXIMUM_MASK_LOAD_TIME)
    }
  }

  startMaskAnimation = () => {
    if (this.maskLoadTimeout) {
      clearTimeout(this.maskLoadTimeout)
    }
    if (this.showMaskQuery.matches) {
      this.setState({ animateMask: true })
    } else {
      this.hideMask()
    }
  }

  maskAnimationDone = event => {
    if (
      event.animationName === wMaskStyle.maskAnimZoomIn ||
      event.animationName === wMaskStyle.maskAnimFadeOut
    ) {
      this.hideMask()
    }
  }

  hideMask = () => {
    this.setState({ showMask: false, animateMask: false })
  }
  render() {
    const {
      angleOrigin,
      position,
      desktopImageUrl,
      mobileImageUrl,
      videoUrl,
      topic,
      title,
      entryType,
      body,
      link,
      centred,
      terms,
      retailerUtility,
      location,
      occurrences,
      utilityPanelModule,
      lazyLoad,
      callsToAction,
      shareButtonType,
      withMask,
      infoAccordion,
      pastEventsAccordion,
      priceInfo,
      analyticsMetadata,
    } = this.props
    const { showMask, animateMask } = this.state
    const ariaLabel = topic ? `${topic} - ${title}` : title
    return (
      <Trackable
        trackingAction={trackingActions.viewPromotion}
        type={trackingTypes.promotion}
        {...analyticsMetadata}
      >
        <CustomThemeContext.Consumer>
          {themeOption => (
            <div
              className={classNames(style.Feature, {
                [style.placeholder]: !desktopImageUrl || !mobileImageUrl,
                [style.Grey]: themeOption.luxury,
                [wMaskStyle.maskedContainer]: showMask,
                [wMaskStyle.moveTheMask]: showMask && animateMask,
              })}
              onAnimationEnd={this.maskAnimationDone}
            >
              {Boolean(desktopImageUrl && mobileImageUrl) && (
                <PotentialLink link={link} aria-label={ariaLabel}>
                  <ResponsiveAsset
                    angleOrigin={angleOrigin}
                    className={classNames({
                      [wMaskStyle.maskedImage]: showMask,
                    })}
                    desktopImageUrl={desktopImageUrl}
                    mobileImageUrl={mobileImageUrl}
                    videoUrl={videoUrl}
                    mobileRatio="standard"
                    desktopRatio="feature"
                    numberOfItems={1}
                    alt={title}
                    lazyLoad={lazyLoad}
                    dataTestId="featured-image"
                    onLoad={withMask ? this.startMaskAnimation : null}
                    onError={withMask ? this.hideMask : null}
                  />
                </PotentialLink>
              )}
              {Boolean(desktopImageUrl && mobileImageUrl) && (
                <div className={style.pattern} aria-hidden="true">
                  <PatternSVG />
                </div>
              )}
              <Container
                type="large"
                className={classNames(
                  containerStyle.container,
                  style.Container,
                  style[position],
                  {
                    [wMaskStyle.maskedContent]: showMask,
                  }
                )}
              >
                <Card
                  className={classNames(style.Card, {
                    [style.centred]: centred,
                  })}
                >
                  <PotentialLink
                    className={style.addLinkStyles}
                    link={link}
                    aria-label={ariaLabel}
                  >
                    {entryType && (
                      <Heading
                        type="extrasmall"
                        className={style.superHeading}
                        data-test-id="featured-type"
                      >
                        {entryType}
                      </Heading>
                    )}
                    <Article
                      shareButtonType={shareButtonType}
                      superheading={topic}
                      heading={title}
                      location={location}
                      occurrences={occurrences}
                      callsToAction={callsToAction}
                      infoAccordion={infoAccordion}
                      pastEventsAccordion={pastEventsAccordion}
                      priceInfo={priceInfo}
                      headerDataTestId="featured-type"
                      dataTestId="featured-title"
                    >
                      <Markdown content={body} />
                      <Markdown content={terms} />
                    </Article>
                  </PotentialLink>
                </Card>
                {utilityPanelModule && (
                  <div className={style.Sidebar}>
                    <CentreUtilityBox
                      {...utilityPanelModule}
                      origin={'feature'}
                    />
                  </div>
                )}
                {retailerUtility && (
                  <div className={style.Sidebar}>
                    <RetailerUtilityBox {...retailerUtility} />
                  </div>
                )}
              </Container>
              {showMask && <WMask className={wMaskStyle.wDeviceMask} />}
            </div>
          )}
        </CustomThemeContext.Consumer>
      </Trackable>
    )
  }
}

Feature.propTypes = {
  angleOrigin: PropTypes.oneOf([
    'bottom-left',
    'top-left',
    'top-right',
    'bottom-right',
  ]),
  position: PropTypes.oneOf(['left', 'right']),
  centred: PropTypes.bool,
  topic: PropTypes.string,
  title: PropTypes.string,
  body: PropTypes.string,
  entryType: PropTypes.string,
  terms: PropTypes.string,
  desktopImageUrl: PropTypes.string,
  mobileImageUrl: PropTypes.string,
  videoUrl: PropTypes.string,
  link: PropTypes.string,
  retailerUtility: PropTypes.object,
  location: PropTypes.string,
  occurrences: occurrencesType,
  lazyLoad: PropTypes.bool,
  callsToAction: callToActionType,
  withMask: PropTypes.bool,
  priceInfo: PropTypes.string,
  infoAccordion: PropTypes.array,
  pastEventsAccordion: PropTypes.array,
  utilityPanelModule: PropTypes.object,
  shareButtonType: PropTypes.string,
  analyticsMetadata: PropTypes.shape({
    correlationId: PropTypes.string,
    title: PropTypes.string,
    promotion: PropTypes.shape({
      creative: PropTypes.string,
    }),
  }),
}

Feature.defaultProps = {
  centred: false,
  terms: '',
  angleOrigin: 'top-right',
  retailerUtility: null,
  lazyLoad: true,
}

export default Feature
